/* -------------------------------------------------------------------------- */
/*                          Componente SlidingPaneEditLocation                          */
/* -------------------------------------------------------------------------- */
// Este componente contiene un SlidingPaneEditLocation genérico que se utiliza en muchas pantallas de la aplicación.
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Button, Col, Row } from 'reactstrap';
import MapsBasic from 'views/components/Map';
import LocationMap from 'views/components/Map/components/LocationMap';
import TabsWrapper from 'views/components/TabsWrapper';
import CustomersDropdown from 'views/formComponents/CustomersDropdown';
import CurrencyDropdownInput from 'views/formComponents/CurrencyDropdownInput';
import LocationClassesDropdownInput from 'views/formComponents/LocationClassesDropdownInput';
import Membership from 'views/formComponents/Membership';
import NumericalInput from 'views/formComponents/NumericalInput';
import TextInput from 'views/formComponents/TextInput';
import TimeInput from 'views/formComponents/TimeInput';
import { DB_FIELD_LENGTHS, NUMERIC_MAX_LENGTHS } from 'utility/constants/dbFieldLengths';
import * as customerSelectors from 'views/screens/Customers/reducers';
import * as authSelectors from '../../../../../redux/reducers/auth';
import * as locationClassesSelectors from '../../../../../redux/reducers/static-catalogs/location-classes';
import { DefaultRootState, StaticCatalogsType } from '../../../../../redux/storeConfig/types';
import moment from 'moment';
import { convertToUserTimezone } from '@src/utility/timezoneUtils';

// Convierte un string de tiempo UTC (ej: "16:00:00" de la DB) a un Date con horas locales
// para que el TimePicker las muestre en la zona horaria del usuario
const utcTimeStrToLocalDate = (timeStr: string | null | undefined): Date | undefined => {
  if (!timeStr) return undefined;
  const m = convertToUserTimezone(`2000-01-01T${timeStr}`);
  const d = new Date();
  d.setHours(m.hours(), m.minutes(), m.seconds(), 0);
  return d;
};

interface Props {
  title: any;
  isOpenSlidingPane: any;
  closeSlidingPane: any;
  subtitle?: any;
  editingLocation: any;
  setEditingLocation?: Function;
  confirmText?: any;
  isInMap?: boolean;
  width?: string;
  hasButtonSubmit?: boolean;
  hasButtonCancel?: boolean;
  action?: any;
  onClose?: any;
  isCustomerLocation?: boolean;
  selectedCustomerId?: number;
  customerRequired?: boolean;
}

const SlidingPaneEditLocation = ({
  title,
  subtitle,
  editingLocation,
  setEditingLocation,
  confirmText,
  isOpenSlidingPane,
  closeSlidingPane,
  hasButtonSubmit = true,
  hasButtonCancel = true,
  action,
  width,
  onClose,
  isInMap = false,
  isCustomerLocation = false,
  selectedCustomerId,
  customerRequired = true
}: Props) => {
  //const [touched, setTouched] = useState(false);

  const { control, handleSubmit, watch, setValue, reset } = useForm();
  const [rerenderMap, setRerenderMap] = useState<Date>(new Date());
  const updateLocationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDraggingRef = useRef(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  const formValues = watch();

  const { store, mapHomeCenter, parsers, customer, userStore, catalogs } = useSelector((state: any) => ({
    store: state,
    mapHomeCenter: authSelectors.getAuthUserMapHomeCenter(state),
    parsers: authSelectors.getParsers(state),
    customer: customerSelectors.getCustomer(state, formValues?.customerId),
    userStore: authSelectors.getUser(state),
    catalogs: state?.staticCatalogs
  }));
  
  const currency: any = Object.values(catalogs?.currencies?.byId || {}).filter(
    (item: any) => item.currencyId === Number(formValues?.billedQuantityCurrencyId)
  )[0];
  
  const initialLatitude = parseFloat(mapHomeCenter[0]);
  const initialLongitude = parseFloat(mapHomeCenter[1]);
  const initialRadius = 50;
  const initialLocationClassIconName = 'Marker1';
  const intl = useIntl();
  const [latitude, setLatitude] = useState<number>(initialLatitude);
  const [longitude, setLongitude] = useState<number>(initialLongitude);

  // Estado interno para el mapa cuando no se usa setEditingLocation
  const [internalMapLocation, setInternalMapLocation] = useState(null);

  useEffect(() => {
    if (isOpenSlidingPane) {
      if (editingLocation) {
        setLatitude(editingLocation.latitude);
        setLongitude(editingLocation.longitude);
        // Inicializar el estado interno del mapa
        setInternalMapLocation({
          ...editingLocation,
          latitude: editingLocation.latitude,
          longitude: editingLocation.longitude
        });
        
        // Reset form with all editingLocation values
        reset({
          locationName: editingLocation.locationName,
          locationAddress: editingLocation.locationAddress,
          latitude: editingLocation.latitude,
          longitude: editingLocation.longitude,
          radius: parsers.convertMetersToAuthUserDistance2System(editingLocation.radius || initialRadius),
          locationClassId: editingLocation.locationClassId,
          customerId: editingLocation.customerId,
          membership: editingLocation.membership,
          distributionChannel: editingLocation.distributionChannel,
          // Time fields
          averageTime: editingLocation.averageTime,
          minTime: editingLocation.minTime,
          maxTime: editingLocation.maxTime,
          startTime: utcTimeStrToLocalDate(editingLocation.startTime),
          endTime: utcTimeStrToLocalDate(editingLocation.endTime),
          // Unit Quantity fields (no conversion needed)
          averageUnitQuantity: editingLocation.averageUnitQuantity ?? undefined,
          minUnitQuantity: editingLocation.minUnitQuantity ?? undefined,
          maxUnitQuantity: editingLocation.maxUnitQuantity ?? undefined,
          // Billed Quantity fields
          averageBilledQuantity: editingLocation.averageBilledQuantity ?? undefined,
          minBilledQuantity: editingLocation.minBilledQuantity ?? undefined,
          maxBilledQuantity: editingLocation.maxBilledQuantity ?? undefined,
          billedQuantityCurrencyId: editingLocation.billedQuantityCurrencyId || parseInt(userStore?.currencyId),
          // Weight fields
          averageWeight: editingLocation.averageWeight ?? undefined,
          minWeight: editingLocation.minWeight ?? undefined,
          maxWeight: editingLocation.maxWeight ?? undefined
        });
      } else {
        setLatitude(initialLatitude);
        setLongitude(initialLongitude);
        // Inicializar con valores por defecto
        setInternalMapLocation({
          latitude: initialLatitude,
          longitude: initialLongitude,
          radius: initialRadius,
          locationClassIconName: initialLocationClassIconName
        });
        
        // Reset form with default values for new location
        reset({
          latitude: initialLatitude,
          longitude: initialLongitude,
          radius: parsers.convertMetersToAuthUserDistance2System(initialRadius)
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenSlidingPane, editingLocation]);

  const updateEditingLocation = useCallback(() => {
    // No actualizar el estado del mapa si estamos arrastrando el marcador
    if (isDraggingRef.current) return;

    const updatedLocation = {
      ...(editingLocation || internalMapLocation || {}),
      ...formValues,
      ...formValues.membership,
      locationClassIconName:
        formValues && formValues.locationClassId
          ? locationClassesSelectors.getLocationClassIconName(store, formValues.locationClassId)
          : initialLocationClassIconName
    };

    if (setEditingLocation) {
      setEditingLocation(updatedLocation);
    } else {
      // Si no hay setEditingLocation, actualizar el estado interno para el mapa
      setInternalMapLocation(updatedLocation);
    }
  }, [setEditingLocation, editingLocation, internalMapLocation, formValues, store, isDraggingRef]);

  // Debounced version of updateEditingLocation para evitar múltiples llamadas
  const debouncedUpdateEditingLocation = useCallback(() => {
    // No hacer nada si estamos arrastrando
    if (isDraggingRef.current) return;

    // Limpiar timeout anterior si existe
    if (updateLocationTimeoutRef.current) {
      clearTimeout(updateLocationTimeoutRef.current);
    }

    // Establecer nuevo timeout
    updateLocationTimeoutRef.current = setTimeout(() => {
      updateEditingLocation();
    }, 300); // 300ms de debounce
  }, [updateEditingLocation]);

  // Memoizamos los valores numéricos para evitar re-renders por cambios de precisión decimal
  const memoizedLocationValues = useMemo(
    () => ({
      latitude: Number(formValues.latitude),
      longitude: Number(formValues.longitude),
      radius: Number(formValues.radius),
      locationClassId: formValues.locationClassId,
      locationName: formValues.locationName
    }),
    [
      Number(formValues.latitude),
      Number(formValues.longitude),
      Number(formValues.radius),
      formValues.locationClassId,
      formValues.locationName
    ]
  );

  useEffect(() => {
    if (
      memoizedLocationValues.latitude != null &&
      memoizedLocationValues.longitude != null &&
      memoizedLocationValues.radius != null &&
      memoizedLocationValues.locationClassId != null
    ) {
      // Siempre ejecutar la actualización, tanto para setEditingLocation como para el estado interno
      debouncedUpdateEditingLocation();
    }
  }, [memoizedLocationValues, debouncedUpdateEditingLocation]);

  // Cleanup del timeout cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (updateLocationTimeoutRef.current) {
        clearTimeout(updateLocationTimeoutRef.current);
      }
    };
  }, []);

  const closeSlidingPaneFunction = (saving) => {
    closeSlidingPane();
    if (onClose != null && !saving) onClose(formValues); //Close without saving
  };

  return (
    <SlidingPane
      className="custom-sliding-pane-form"
      overlayClassName={isInMap ? 'custom-overlay-sliding-pane-map' : 'custom-overlay-sliding-pane'}
      isOpen={isOpenSlidingPane}
      title={title}
      subtitle={subtitle}
      width={width || '50rem'}
      onRequestClose={() => closeSlidingPaneFunction(false)}
    >
      <TabsWrapper
        initialActiveTab={0}
        activeTab={selectedTab}
        setActiveTab={setSelectedTab}
        useCardContent={false}
        tabs={[
          {
            tabId: 0,
            icon: 'MapPin',
            title: 'locations.location'
          },
          {
            tabId: 1,
            icon: 'Clock',
            title: 'locations.additionalInformation'
          }
        ]}
        tabsContent={[
          {
            tabId: 0,
            component: (
              <Row className={'d-flex justify-content-center'}>
        <Col sm="12">
          <Controller
            control={control}
            shouldUnregister
            name={'locationClassId'}
            defaultValue={
              editingLocation && editingLocation.locationClassId
                ? editingLocation.locationClassId
                : isCustomerLocation
                ? 34
                : undefined
            }
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <LocationClassesDropdownInput
                field={field}
                fieldstate={fieldState}
                id={'inputLocationClassId'}
                additionalonchangefunction={(value) => {
                  setRerenderMap(new Date());
                }}
                required={true}
                isDisabled={isCustomerLocation}
              />
            )}
          />
        </Col>
        {(formValues?.locationClassId === 34 || formValues?.locationClassId === 30) &&
          customerRequired && (
            <>
              <Col sm="12">
                <Controller
                  control={control}
                  shouldUnregister
                  name={'customerId'}
                  rules={{ required: customerRequired }}
                  defaultValue={
                    isCustomerLocation ? selectedCustomerId : editingLocation?.customerId
                  }
                  render={({ field, fieldState }) => (
                    <CustomersDropdown
                      field={field}
                      fieldstate={fieldState}
                      id={'customerIdInput'}
                      required={customerRequired}
                      enableSelectLocation
                      isInModal
                      locationId={editingLocation?.locationId}
                      customersFilters={formValues?.membership}
                      isDisabled={isCustomerLocation && selectedCustomerId}
                      appendAddCustomer={isCustomerLocation && selectedCustomerId ? false : true}
                    />
                  )}
                />
              </Col>
            </>
          )}
        <Col sm="12">
          <Controller
            control={control}
            shouldUnregister
            name={'locationName'}
            defaultValue={
              editingLocation && editingLocation.locationName
                ? editingLocation.locationName
                : undefined
            }
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <TextInput
                id={'inputLocationName'}
                field={field}
                fieldstate={fieldState}
                required={true}
                label={intl.formatMessage({ id: 'locations.name' })}
                maxLength={DB_FIELD_LENGTHS.Locations.LocationName}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            shouldUnregister
            name={'locationAddress'}
            defaultValue={
              editingLocation && editingLocation.locationAddress
                ? editingLocation.locationAddress
                : undefined
            }
            render={({ field, fieldState }) => (
              <TextInput
                id={'inputLocationAddress'}
                field={field}
                fieldstate={fieldState}
                label={intl.formatMessage({ id: 'locations.locationAddress' })}
                maxLength={DB_FIELD_LENGTHS.Locations.LocationAddress}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            shouldUnregister
            name={'latitude'}
            defaultValue={
              editingLocation && editingLocation.latitude != null
                ? editingLocation.latitude
                : undefined
            }
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <NumericalInput
                field={field}
                fieldstate={fieldState}
                required={true}
                id={'inputLatitude'}
                label={intl.formatMessage({ id: 'locations.latitude' })}
                additionalonchangefunction={(value) => {
                  setRerenderMap(new Date());
                }}
                allowUndefined={false}
                useNumberSpinner={true}
                allowDecimals={true}
                decimalsPrecision={10}
                step={0.000001}
                min={-90}
                max={90}
                maxLength={NUMERIC_MAX_LENGTHS.LATITUDE}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            shouldUnregister
            name={'longitude'}
            defaultValue={
              editingLocation && editingLocation.longitude != null
                ? editingLocation.longitude
                : undefined
            }
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <NumericalInput
                field={field}
                fieldstate={fieldState}
                required={true}
                id={'inputLongitude'}
                label={intl.formatMessage({ id: 'locations.longitude' })}
                additionalonchangefunction={(value) => {
                  setRerenderMap(new Date());
                }}
                allowUndefined={false}
                useNumberSpinner={true}
                allowDecimals={true}
                decimalsPrecision={10}
                step={0.000001}
                min={-180}
                max={180}
                maxLength={NUMERIC_MAX_LENGTHS.LONGITUDE}
              />
            )}
          />
        </Col>
        <Col sm="12">
          <Controller
            control={control}
            shouldUnregister
            name={'radius'}
            defaultValue={parsers.convertMetersToAuthUserDistance2System(
              editingLocation && editingLocation.radius ? editingLocation.radius : initialRadius
            )}
            rules={{ required: true }}
            render={({ field, fieldState }) => (
              <NumericalInput
                field={field}
                fieldstate={fieldState}
                required={true}
                id={'inputRadius'}
                label={intl.formatMessage({ id: 'locations.radius' })}
                allowUndefined={false}
                useNumberSpinner={true}
                allowDecimals={true}
                additionalonchangefunction={(value) => {
                  setRerenderMap(new Date());
                }}
                step={1}
                metric={'distance2'}
                min={parsers.convertMetersToAuthUserDistance2System(10)}
                max={parsers.convertMetersToAuthUserDistance2System(100000)}
                maxLength={NUMERIC_MAX_LENGTHS.RADIUS_METERS}
              />
            )}
          />
        </Col>
        {!isInMap && (
          <Col sm="12">
            <LocationMap
              mapOptions={{
                className: '',
                style: { height: '40vh' },
                rerenderMap: rerenderMap
              }}
              locationEditOptions={{
                location: {
                  ...(setEditingLocation ? editingLocation : internalMapLocation),
                  // Usar siempre los valores más actualizados del formulario o del estado local
                  latitude: formValues.latitude || latitude,
                  longitude: formValues.longitude || longitude,
                  radius:
                    formValues.radius ||
                    (formValues && formValues.radius ? formValues.radius : initialRadius),
                  locationClassIconName:
                    formValues && formValues.locationClassId
                      ? locationClassesSelectors.getLocationClassIconName(
                          store,
                          formValues.locationClassId
                        )
                      : initialLocationClassIconName
                },
                onLocationDragend: (values) => {
                  // Marcar que el drag ha terminado
                  isDraggingRef.current = false;

                  setLatitude(values.lat);
                  setLongitude(values.lng);
                  // Actualizar los valores del formulario cuando se arrastra el marker
                  setValue('latitude', values.lat);
                  setValue('longitude', values.lng);
                  // Actualizar inmediatamente el estado interno del mapa para evitar el rebote
                  setInternalMapLocation((prev) => ({
                    ...prev,
                    latitude: values.lat,
                    longitude: values.lng
                  }));
                  // Actualizar también editingLocation si existe
                  if (setEditingLocation) {
                    setEditingLocation((prev) => ({
                      ...prev,
                      latitude: values.lat,
                      longitude: values.lng
                    }));
                  }
                },
                onLocationDragstart: () => {
                  // Marcar que estamos iniciando el drag
                  isDraggingRef.current = true;
                },
                showLocation: true,
                useRectangleBounds: true,
                isMapFullScreen: false,
                usePopovers: false
              }}
            />
            <br />
          </Col>
        )}
        <Col sm="12">
          <Controller
            control={control}
            shouldUnregister
            name={'privateCode'}
            defaultValue={
              editingLocation && editingLocation.privateCode
                ? editingLocation.privateCode
                : undefined
            }
            render={({ field, fieldState }) => (
              <TextInput
                id={'inputPrivateCode'}
                field={field}
                fieldstate={fieldState}
                label={intl.formatMessage({ id: 'locations.privateCode' })}
                maxLength={DB_FIELD_LENGTHS.Locations.PrivateCode}
              />
            )}
          />
        </Col>
        <Membership
          name={'membership'}
          control={control}
          options={{
            colSize: '12',
            useOperator: false,
            useUserOrganization: true,
            setUserOrganization: true,
            showAllDivision: true,
            showAllSubdivision: true
          }}
          defaultValue={
            editingLocation
              ? {
                  operatorId: editingLocation.operatorId,
                  organizationId: editingLocation.organizationId,
                  divisionId: editingLocation.divisionId,
                  subdivisionId: editingLocation.subdivisionId
                }
              : undefined
          }
        />
              </Row>
            )
          },
          {
            tabId: 1,
            component: (
              <Row className={'d-flex justify-content-center'}>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'distributionChannel'}
                    defaultValue={editingLocation?.distributionChannel}
                    render={({ field, fieldState }) => (
                      <TextInput
                        id={'inputDistributionChannel'}
                        field={field}
                        fieldstate={fieldState}
                        label={intl.formatMessage({ id: 'locations.distributionChannel' })}
                        maxLength={DB_FIELD_LENGTHS.Locations.DistributionChannel}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'averageTime'}
                    defaultValue={editingLocation?.averageTime}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputAverageTime'}
                        field={field}
                        fieldstate={fieldState}
                        label={intl.formatMessage({ id: 'locations.averageTime' })}
                        appendcomponent={intl.formatMessage({ id: 'common.minutes' })}
                        min={formValues?.minTime ?? 0}
                        max={formValues?.maxTime ?? 1000}
                        maxLength={NUMERIC_MAX_LENGTHS.DURATION_MINUTES}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'minTime'}
                    defaultValue={editingLocation?.minTime}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputMinTime'}
                        field={field}
                        fieldstate={fieldState}
                        label={intl.formatMessage({ id: 'locations.minTimeStay' })}
                        appendcomponent={intl.formatMessage({ id: 'common.minutes' })}
                        min={0}
                        max={formValues?.maxTime ?? 1000}
                        maxLength={NUMERIC_MAX_LENGTHS.DURATION_MINUTES}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'maxTime'}
                    defaultValue={editingLocation?.maxTime}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputMaxTime'}
                        field={field}
                        fieldstate={fieldState}
                        label={intl.formatMessage({ id: 'locations.maxTimeStay' })}
                        appendcomponent={intl.formatMessage({ id: 'common.minutes' })}
                        min={formValues?.minTime ?? 0}
                        max={1000}
                        maxLength={NUMERIC_MAX_LENGTHS.DURATION_MINUTES}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'startTime'}
                    defaultValue={utcTimeStrToLocalDate(editingLocation?.startTime)}
                    render={({ field, fieldState }) => (
                      <TimeInput
                        field={field}
                        fieldstate={fieldState}
                        allowNulls
                        required={false}
                        label={intl.formatMessage({ id: 'locations.startTime' })}
                        maxTime={
                          formValues?.endTime
                            ? moment(formValues.endTime).format('HH:mm')
                            : undefined
                        }
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'endTime'}
                    defaultValue={utcTimeStrToLocalDate(editingLocation?.endTime)}
                    render={({ field, fieldState }) => (
                      <TimeInput
                        field={field}
                        fieldstate={fieldState}
                        allowNulls
                        required={false}
                        label={intl.formatMessage({ id: 'locations.endTime' })}
                        minTime={
                          formValues?.startTime
                            ? moment(formValues.startTime).format('HH:mm')
                            : undefined
                        }
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'averageUnitQuantity'}
                    defaultValue={editingLocation?.averageUnitQuantity ?? undefined}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputAverageUnitQuantity'}
                        field={field}
                        fieldstate={fieldState}
                        allowDecimals
                        decimalsPrecision={2}
                        label={intl.formatMessage({ id: 'locations.averageUnitQuantity' })}
                        min={formValues?.minUnitQuantity ?? 0}
                        max={formValues?.maxUnitQuantity ?? 1000}
                        maxLength={NUMERIC_MAX_LENGTHS.QUANTITY}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'minUnitQuantity'}
                    defaultValue={editingLocation?.minUnitQuantity ?? undefined}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputMinUnitQuantity'}
                        field={field}
                        fieldstate={fieldState}
                        allowDecimals
                        decimalsPrecision={2}
                        label={intl.formatMessage({ id: 'locations.minUnitQuantity' })}
                        min={0}
                        max={formValues?.maxUnitQuantity ?? 1000}
                        maxLength={NUMERIC_MAX_LENGTHS.QUANTITY}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'maxUnitQuantity'}
                    defaultValue={editingLocation?.maxUnitQuantity ?? undefined}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputMaxUnitQuantity'}
                        field={field}
                        fieldstate={fieldState}
                        allowDecimals
                        decimalsPrecision={2}
                        label={intl.formatMessage({ id: 'locations.maxUnitQuantity' })}
                        min={formValues?.minUnitQuantity ?? 0}
                        max={1000}
                        maxLength={NUMERIC_MAX_LENGTHS.QUANTITY}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    name={'billedQuantityCurrencyId'}
                    defaultValue={editingLocation?.billedQuantityCurrencyId || parseInt(userStore?.currencyId)}
                    render={({ field, fieldState }) => (
                      <CurrencyDropdownInput
                        field={field}
                        fieldstate={fieldState}
                        id={'billedQuantityCurrencyId'}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'averageBilledQuantity'}
                    defaultValue={editingLocation?.averageBilledQuantity ?? undefined}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputAverageBilledQuantity'}
                        field={field}
                        fieldstate={fieldState}
                        allowDecimals
                        decimalsPrecision={2}
                        label={intl.formatMessage({ id: 'locations.averageBilledQuantity' })}
                        appendcomponent={currency?.codeAlpha || ''}
                        min={formValues?.minBilledQuantity ?? 0}
                        max={formValues?.maxBilledQuantity ?? 999999999}
                        maxLength={NUMERIC_MAX_LENGTHS.PRICE}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'minBilledQuantity'}
                    defaultValue={editingLocation?.minBilledQuantity ?? undefined}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputMinBilledQuantity'}
                        field={field}
                        fieldstate={fieldState}
                        allowDecimals
                        decimalsPrecision={2}
                        label={intl.formatMessage({ id: 'locations.minBilledQuantity' })}
                        appendcomponent={currency?.codeAlpha || ''}
                        min={0}
                        max={formValues?.maxBilledQuantity ?? 999999999}
                        maxLength={NUMERIC_MAX_LENGTHS.PRICE}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'maxBilledQuantity'}
                    defaultValue={editingLocation?.maxBilledQuantity ?? undefined}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputMaxBilledQuantity'}
                        field={field}
                        fieldstate={fieldState}
                        allowDecimals
                        decimalsPrecision={2}
                        label={intl.formatMessage({ id: 'locations.maxBilledQuantity' })}
                        appendcomponent={currency?.codeAlpha || ''}
                        min={formValues?.minBilledQuantity ?? 0}
                        max={999999999}
                        maxLength={NUMERIC_MAX_LENGTHS.PRICE}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'averageWeight'}
                    defaultValue={editingLocation?.averageWeight ?? undefined}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputAverageWeight'}
                        field={field}
                        fieldstate={fieldState}
                        allowDecimals
                        decimalsPrecision={2}
                        label={intl.formatMessage({ id: 'locations.averageWeight' })}
                        min={formValues?.minWeight ?? 0}
                        max={formValues?.maxWeight ?? 1000}
                        maxLength={NUMERIC_MAX_LENGTHS.QUANTITY}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'minWeight'}
                    defaultValue={editingLocation?.minWeight ?? undefined}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputMinWeight'}
                        field={field}
                        fieldstate={fieldState}
                        allowDecimals
                        decimalsPrecision={2}
                        label={intl.formatMessage({ id: 'locations.minWeight' })}
                        min={0}
                        max={formValues?.maxWeight ?? 1000}
                        maxLength={NUMERIC_MAX_LENGTHS.QUANTITY}
                      />
                    )}
                  />
                </Col>
                <Col sm="12">
                  <Controller
                    control={control}
                    shouldUnregister
                    name={'maxWeight'}
                    defaultValue={editingLocation?.maxWeight ?? undefined}
                    render={({ field, fieldState }) => (
                      <NumericalInput
                        id={'inputMaxWeight'}
                        field={field}
                        fieldstate={fieldState}
                        allowDecimals
                        decimalsPrecision={2}
                        label={intl.formatMessage({ id: 'locations.maxWeight' })}
                        min={formValues?.minWeight ?? 0}
                        max={1000}
                        maxLength={NUMERIC_MAX_LENGTHS.QUANTITY}
                      />
                    )}
                  />
                </Col>
              </Row>
            )
          }
        ]}
      />
        <Row className={'footer-slide-pane'}>
          <Col sm="12 d-flex ">
            {hasButtonSubmit && (
              <Button.Ripple
                className="mr-1 mt-50 mb-50"
                color="primary"
                /*type="submit"*/
                onClick={handleSubmit((values) => {
                  closeSlidingPaneFunction(true);
                  action({
                    ...values,
                    customerId: values.customerId,
                    radius: parsers.convertAuthUserDistance2SystemToMeters(values.radius),
                    // Convertir los tiempos a formato HH:mm:ss
                    startTime: values.startTime
                      ? moment(values.startTime).utc().format('HH:mm:ss')
                      : undefined,
                    endTime: values.endTime ? moment(values.endTime).utc().format('HH:mm:ss') : undefined
                  });
                })}
              >
                {confirmText || <FormattedMessage id="common.save" />}
              </Button.Ripple>
            )}
            {hasButtonCancel && (
              <Button.Ripple
                className="mr-1 mt-50 mb-50"
                color="light"
                onClick={() => closeSlidingPaneFunction(false)}
              >
                <FormattedMessage id="common.cancel" />
              </Button.Ripple>
            )}
          </Col>
        </Row>
    </SlidingPane>
  );
};

export default SlidingPaneEditLocation;
